/**
 * UserTypeSelector — Toggle between INDIVIDUAL and ORGANISATION.
 *
 * Renders as a pair of tab-like buttons.
 * Props:
 *   value           — "INDIVIDUAL" | "ORGANISATION"
 *   onChange         — (newValue) => void
 *   disabled         — boolean
 *   allowIndividual  — boolean (default false). When false, only ORGANISATION is shown.
 */
import React, { useEffect } from 'react';
import { useTheme } from '../../theme/ThemeContext';

const TYPES = [
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'ORGANISATION', label: 'Organisation' },
];

export default function UserTypeSelector({ value, onChange, disabled, allowIndividual = false }) {
  const { theme } = useTheme();
  const isDark = theme.auth.containerBg === '#111827';

  const visibleTypes = allowIndividual ? TYPES : TYPES.filter((t) => t.value !== 'INDIVIDUAL');

  // If individual is hidden but currently selected, force to ORGANISATION
  useEffect(() => {
    if (!allowIndividual && value === 'INDIVIDUAL') {
      onChange('ORGANISATION');
    }
  }, [allowIndividual, value, onChange]);

  // Don't render the selector at all if only one option
  if (visibleTypes.length <= 1) return null;

  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 8, borderRadius: 10, overflow: 'hidden' }}>
      {visibleTypes.map((t) => {
        const isActive = value === t.value;
        return (
          <button
            key={t.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(t.value)}
            style={{
              flex: 1,
              padding: '10px 14px',
              fontSize: 13,
              fontWeight: isActive ? 600 : 500,
              letterSpacing: '0.2px',
              border: `2px solid ${isActive ? (isDark ? '#0d9488' : '#5eead4') : (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)')}`,
              borderRadius: 10,
              cursor: disabled ? 'not-allowed' : 'pointer',
              background: isActive
                ? isDark
                  ? 'linear-gradient(135deg, rgba(13, 148, 136, 0.15) 0%, rgba(13, 148, 136, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(15, 118, 110, 0.12) 0%, rgba(15, 118, 110, 0.04) 100%)'
                : isDark
                  ? 'rgba(255, 255, 255, 0.03)'
                  : 'rgba(0, 0, 0, 0.015)',
              color: isActive ? (isDark ? '#5eead4' : '#0f766e') : theme.auth.mutedText,
              transition: 'all 0.3s ease',
              opacity: disabled ? 0.6 : 1,
              boxShadow: isActive
                ? isDark
                  ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 0 12px rgba(13, 148, 136, 0.2)'
                  : 'inset 0 1px 0 rgba(255, 255, 255, 0.4), 0 0 8px rgba(15, 118, 110, 0.1)'
                : 'none',
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
